/**
 * @file Helper functions for cache refetching and invalidation for RTK Query
 * @see https://redux-toolkit.js.org/rtk-query/usage/automated-refetching
 */

import { FetchBaseQueryError, TagDescription } from '@reduxjs/toolkit/query';
import { PaginatedResponse } from './types';

type ResultDescription<
  Result,
  Error extends FetchBaseQueryError | undefined,
  Args,
  Tag extends string,
> = (result: Result, error: Error, args: Args) => TagDescription<Tag>[];

/**
 * Invalidate tags only if there was no error
 * @param tags - The tags to invalidate or a function that returns tags based on the result
 * @returns The tags to invalidate, or an empty array if there was an error
 * @example
 * ```ts
 * invalidateTagsOnSuccess(['Customer']);
 * ```
 * @example
 * ```ts
 * invalidateTagsOnSuccess((result) => [{ type: 'Customer', id: result.payLinkCustomerId }]);
 * ```
 */
export const invalidateTagsOnSuccess = <
  Result,
  Error extends FetchBaseQueryError | undefined,
  Args,
  Tag extends string,
>(
  tags: TagDescription<Tag>[] | ResultDescription<Result, Error, Args, Tag>,
): ResultDescription<Result | undefined, Error, Args, Tag> => {
  return (result, error, args) => {
    if (error || !result) return [];
    return typeof tags === 'function' ? tags(result, error, args) : tags;
  };
};

const tagIdMapping = {
  Customer: 'payLinkCustomerId',
} as const;
type TagIdMapping = typeof tagIdMapping;

/**
 * Provides a list of tags for a given tag type.
 * A paginated response is expected.
 * @param result - The result from the query
 * @param tagType - The tag type
 * @param listId - The id of the list
 * @returns The tags to provide
 * @example
 * ```ts
 * providesPaginatedList(result, 'Customer');
 * ```
 */
export const providesPaginatedList = <
  TTag extends keyof TagIdMapping,
  TData extends Record<TagIdMapping[TTag], string | number>,
>(
  result: PaginatedResponse<TData> | undefined,
  tagType: TTag,
  listId: string = 'LIST',
) => {
  const idField = tagIdMapping[tagType];

  if (!result?.data) return [];

  return [
    { type: tagType, id: listId },
    ...result.data.map((item) => ({ type: tagType, id: item[idField] })),
  ];
};

/**
 * Provides a list of tags for a given tag type.
 * @param result - The result from the query
 * @param tagType - The tag type
 * @param listId - The id of the list
 * @returns The tags to provide
 * @example
 * ```ts
 * providesList(result, 'Customer', 'CARD_ON_FILE');
 * ```
 */
export const providesList = <
  TTag extends keyof TagIdMapping,
  TData extends Record<TagIdMapping[TTag], string | number>,
>(
  result: TData[] | undefined,
  tagType: TTag,
  listId: string = 'LIST',
) => {
  const idField = tagIdMapping[tagType];

  if (!result) return [];

  return [
    { type: tagType, id: listId },
    ...result.map((item) => ({ type: tagType, id: item[idField] })),
  ];
};
